import { OpenAPI as InventoryOpenAPI } from "@zenith/services/inventory/core/OpenAPI";
import { OpenAPI as TransactionOpenAPI } from "@zenith/services/transactions/core/OpenAPI";
import { OpenAPI as UserOpenAPI } from "@zenith/services/user/core/OpenAPI";
import { generateTraceHeaders } from "@zenith/utils/tracing";

/**
 * Journey session identity (Paygilant spec §4).
 *
 * A server-minted correlation key that spans the whole journey, from first page
 * load through logout. Paygilant joins the telemetry its Web SDK collects to the
 * checkpoint calls our backend makes, keyed on this id, so the SDK and every API
 * call must carry the same value.
 *
 * It is not a credential. Every failure here is swallowed: without an id the app
 * works exactly as before, just without Paygilant correlation.
 */

export const JOURNEY_SESSION_HEADER = "X-Journey-Session-Id";

const STORAGE_KEY = "journeySessionId";
const SESSION_ENDPOINT = "/api/risk/session";
const REQUEST_TIMEOUT_MS = 5000;

// Stored in a VARCHAR(64) column and sent as a header value.
const VALID_ID = /^[A-Za-z0-9-]{1,64}$/;

let inFlight: Promise<string | undefined> | undefined;
let generation = 0;

export function getJourneySessionId(): string | undefined {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function journeySessionHeaders(): Record<string, string> {
  const id = getJourneySessionId();
  return id ? { [JOURNEY_SESSION_HEADER]: id } : {};
}

/**
 * True for requests to our own APIs. The header must not reach third-party hosts:
 * a custom header triggers a CORS preflight they may reject, breaking the call.
 */
export function isPlatformApiRequest(url: string | undefined): boolean {
  if (!url) return false;
  // Relative URLs go to our own origin; protocol-relative ones ("//host") do not
  if (!/^([a-z][a-z\d+.-]*:)?\/\//i.test(url)) return true;
  return [
    UserOpenAPI.BASE,
    TransactionOpenAPI.BASE,
    InventoryOpenAPI.BASE,
  ].some((base) => Boolean(base) && url.startsWith(base));
}

function storeJourneySessionId(id: string | undefined) {
  try {
    if (id) {
      sessionStorage.setItem(STORAGE_KEY, id);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private mode quota, disabled storage) - run without an id.
  }
}

async function requestJourneySessionId(): Promise<string | undefined> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    // Re-presenting a held id makes the server echo it back while it is still
    // valid, so a reload stays on the same journey.
    const response = await fetch(`${UserOpenAPI.BASE}${SESSION_ENDPOINT}`, {
      headers: {
        Accept: "application/json",
        ...generateTraceHeaders(),
        ...journeySessionHeaders(),
      },
      signal: controller.signal,
    });
    if (!response.ok) return undefined;

    const body = await response.json();
    // Accept both a bare body and the platform's ApiResponseDTO envelope.
    const id = body?.data?.journeySessionId ?? body?.journeySessionId;
    return typeof id === "string" && VALID_ID.test(id) ? id : undefined;
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

function refresh(): Promise<string | undefined> {
  const startedIn = generation;
  inFlight ??= requestJourneySessionId()
    .then((id) => {
      // A journey cleared while this was in flight must stay cleared.
      if (startedIn === generation) storeJourneySessionId(id);
      return id;
    })
    .finally(() => {
      inFlight = undefined;
    });
  return inFlight;
}

/** Resolves the journey id for this page load, re-presenting any id already held. */
export function initJourneySession(): Promise<string | undefined> {
  return refresh();
}

/**
 * Discards the current journey (logout, session timeout). The next page load
 * starts a new one, since there is no id left to re-present.
 */
export function clearJourneySession() {
  generation += 1;
  storeJourneySessionId(undefined);
}
